/* OFFGRD video carousel.
 *
 * One clip visible at a time; advancing translates the track left. The track
 * advances when the current clip fires 'ended', so a slide is never cut off
 * mid-clip and no per-clip timing needs maintaining.
 *
 * Only the current clip ever plays — neighbours are paused and rewound, so a
 * phone never decodes three videos at once.
 *
 * Deliberately NOT hidden behind prefers-reduced-motion: that setting should
 * stop automatic motion, not remove the footage. Reduced-motion users get
 * native controls and manual navigation.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function paintFirstFrame(video) {
    if (video.readyState >= 2) return;
    try {
      video.currentTime = 0.1;
    } catch (e) {
      /* seeking before metadata throws in older Safari — harmless */
    }
  }

  function init(root) {
    var track = root.querySelector('[data-vs-track]');
    var slides = root.querySelectorAll('[data-vs-slide]');
    if (!track || !slides.length) return;

    var videos = root.querySelectorAll('.offgrd-vs__video');
    var dots = root.querySelectorAll('[data-vs-dot]');
    var prev = root.querySelector('[data-vs-prev]');
    var next = root.querySelector('[data-vs-next]');
    var autoplay = root.getAttribute('data-autoplay') !== 'false' && !reduce;

    var index = 0;
    var visible = false;
    var advanceTimer = null;

    function stopAll() {
      Array.prototype.forEach.call(videos, function (v) {
        if (!v.paused) v.pause();
      });
    }

    function playCurrent() {
      if (!visible || reduce) return;
      var v = videos[index];
      if (!v) return;
      var p = v.play();
      if (p && p.catch) {
        // Autoplay refused (data saver, iOS Low Power). Surface controls
        // rather than leaving a still frame with no way to start it.
        p.catch(function () {
          v.controls = true;
          paintFirstFrame(v);
        });
      }
    }

    function go(n, userInitiated) {
      var count = slides.length;
      index = ((n % count) + count) % count;

      track.style.translate = '-' + index * 100 + '% 0';

      Array.prototype.forEach.call(slides, function (s, i) {
        s.classList.toggle('is-current', i === index);
      });
      Array.prototype.forEach.call(dots, function (d, i) {
        d.classList.toggle('is-on', i === index);
        d.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      stopAll();
      Array.prototype.forEach.call(videos, function (v, i) {
        if (i !== index) {
          try { v.currentTime = 0.1; } catch (e) { /* not yet seekable */ }
        }
      });

      // A manual jump cancels any pending fallback advance so the two do not
      // race and skip a slide.
      if (advanceTimer) {
        clearTimeout(advanceTimer);
        advanceTimer = null;
      }

      playCurrent();
      if (userInitiated && autoplay) armFallback();
    }

    // 'ended' is the primary advance. This is only a safety net for a stalled
    // download, where 'ended' would never fire and the carousel would freeze.
    function armFallback() {
      if (!autoplay) return;
      if (advanceTimer) clearTimeout(advanceTimer);
      var v = videos[index];
      var ms = 12000;
      if (v && isFinite(v.duration) && v.duration > 0) ms = (v.duration + 1.5) * 1000;
      advanceTimer = setTimeout(function () {
        go(index + 1);
        armFallback();
      }, ms);
    }

    Array.prototype.forEach.call(videos, function (v, i) {
      v.addEventListener('loadedmetadata', function () {
        paintFirstFrame(v);
        if (i === index) armFallback();
      });

      v.addEventListener('ended', function () {
        if (i !== index) return;
        if (autoplay) {
          go(index + 1);
          armFallback();
        } else {
          // Not auto-advancing: loop the clip in place.
          try { v.currentTime = 0; } catch (e) { /* ignore */ }
          playCurrent();
        }
      });

      if (reduce) {
        v.controls = true;
      }
    });

    if (prev) prev.addEventListener('click', function () { go(index - 1, true); });
    if (next) next.addEventListener('click', function () { go(index + 1, true); });
    Array.prototype.forEach.call(dots, function (d, i) {
      d.addEventListener('click', function () { go(i, true); });
    });

    // Keyboard support for the carousel region.
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { go(index - 1, true); }
      else if (e.key === 'ArrowRight') { go(index + 1, true); }
    });

    // Nothing downloads or plays until the section is near the viewport.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            visible = entry.isIntersecting;
            if (visible) {
              playCurrent();
              armFallback();
            } else {
              stopAll();
              if (advanceTimer) {
                clearTimeout(advanceTimer);
                advanceTimer = null;
              }
            }
          });
        },
        { rootMargin: '200px 0px', threshold: 0.25 }
      );
      io.observe(root);
    } else {
      visible = true;
      playCurrent();
      armFallback();
    }

    // Background tabs: stop decoding, resume on return.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopAll();
        if (advanceTimer) { clearTimeout(advanceTimer); advanceTimer = null; }
      } else if (visible) {
        playCurrent();
        armFallback();
      }
    });

    go(0);
  }

  function boot() {
    document.querySelectorAll('[data-offgrd-video-carousel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-offgrd-video-carousel]');
    if (el) init(el);
  });
})();
